"use client";

import { CalendarRange, Footprints, Layers, ListChecks, ListOrdered, Puzzle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import FlashcardDeck from "@/components/interactives/FlashcardDeck";
import KnowledgeCheck from "@/components/interactives/KnowledgeCheck";
import MatchingActivity, { type MatchEntry } from "@/components/interactives/MatchingActivity";
import SequencingActivity, {
  type SequencingStep,
} from "@/components/interactives/SequencingActivity";
import StepRevealActivity from "@/components/interactives/StepRevealActivity";
import TimelineActivity from "@/components/interactives/TimelineActivity";
import {
  DEMO_FLASHCARDS,
  DEMO_MATCH_LEFTS,
  DEMO_MATCH_RIGHTS,
  DEMO_QUESTIONS,
  DEMO_SEQUENCE,
  DEMO_TIMELINE,
  DEMO_WORKED,
} from "./demoContent";

const TABS = [
  { key: "flashcards", label: "Flashcards", icon: Layers },
  { key: "matching", label: "Matching", icon: Puzzle },
  { key: "sequencing", label: "Ordering", icon: ListOrdered },
  { key: "timeline", label: "Timeline", icon: CalendarRange },
  { key: "knowledge_check", label: "Knowledge check", icon: ListChecks },
  { key: "step_reveal", label: "Worked example", icon: Footprints },
] as const;

type TabKey = (typeof TABS)[number]["key"];

function shuffled<T>(items: T[]): T[] {
  const arr = [...items];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export default function Playground() {
  const [tab, setTab] = useState<TabKey>("flashcards");
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Shuffle after mount: these components run entirely client-side here (the
  // editor-preview pattern), and shuffling during SSR would break hydration.
  const [rights, setRights] = useState<MatchEntry[] | null>(null);
  const [steps, setSteps] = useState<SequencingStep[] | null>(null);
  useEffect(() => {
    setRights(shuffled(DEMO_MATCH_RIGHTS));
    setSteps(shuffled(DEMO_SEQUENCE));
  }, []);

  const onTablistKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
    event.preventDefault();
    const current = TABS.findIndex((t) => t.key === tab);
    const delta = event.key === "ArrowRight" ? 1 : -1;
    const next = (current + delta + TABS.length) % TABS.length;
    setTab(TABS[next].key);
    tabRefs.current[next]?.focus();
  };

  const correctOrder = DEMO_SEQUENCE.map((s) => s.id);

  return (
    <section id="playground" className="scroll-mt-16 border-y border-line bg-surface">
      <div className="mx-auto max-w-3xl px-6 py-20 lg:py-24">
        <div className="text-center">
          <p className="ld-eyebrow">Playground</p>
          <h2 className="ld-display mt-2 text-2xl font-bold sm:text-3xl">
            Try it — this is the real thing
          </h2>
          <p className="mx-auto mt-4 max-w-xl leading-relaxed text-muted">
            These aren&apos;t mockups: they&apos;re the same activity components students
            get on lesson pages, running a mini-lesson on how learning actually
            works. Drag, flip, and check — keyboard works too.
          </p>
        </div>

        <div role="tablist" aria-label="Activity types" className="mt-8 flex flex-wrap justify-center gap-2">
          {TABS.map((t, i) => (
            <button
              key={t.key}
              ref={(el) => {
                tabRefs.current[i] = el;
              }}
              role="tab"
              id={`ld-tab-${t.key}`}
              aria-selected={tab === t.key}
              aria-controls={`ld-panel-${t.key}`}
              tabIndex={tab === t.key ? 0 : -1}
              onKeyDown={onTablistKeyDown}
              onClick={() => setTab(t.key)}
              className="ld-tab"
            >
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* All panels stay mounted (hidden) so progress survives tab switches */}
        <div className="mt-6">
          <div
            role="tabpanel"
            id="ld-panel-flashcards"
            aria-labelledby="ld-tab-flashcards"
            hidden={tab !== "flashcards"}
          >
            <FlashcardDeck title="Why retrieval beats rereading" cards={DEMO_FLASHCARDS} />
          </div>
          <div
            role="tabpanel"
            id="ld-panel-matching"
            aria-labelledby="ld-tab-matching"
            hidden={tab !== "matching"}
          >
            {rights && (
              <MatchingActivity
                title="Match the memory term to its meaning"
                lefts={DEMO_MATCH_LEFTS}
                rights={rights}
                checkMatch={async (leftId, rightId) => ({ correct: leftId === rightId })}
              />
            )}
          </div>
          <div
            role="tabpanel"
            id="ld-panel-sequencing"
            aria-labelledby="ld-tab-sequencing"
            hidden={tab !== "sequencing"}
          >
            {steps && (
              <SequencingActivity
                title="Build a durable memory"
                prompt="Arrange the stages in the order they happen."
                steps={steps}
                check={async (order) => {
                  const positions = order.map((id, i) => id === correctOrder[i]);
                  return { positions, all_correct: positions.every(Boolean) };
                }}
              />
            )}
          </div>
          <div
            role="tabpanel"
            id="ld-panel-timeline"
            aria-labelledby="ld-tab-timeline"
            hidden={tab !== "timeline"}
          >
            <TimelineActivity
              title="Milestones in the science of learning"
              prompt="A timeline you can read top to bottom — each event in context."
              steps={DEMO_TIMELINE}
            />
          </div>
          <div
            role="tabpanel"
            id="ld-panel-knowledge_check"
            aria-labelledby="ld-tab-knowledge_check"
            hidden={tab !== "knowledge_check"}
          >
            <KnowledgeCheck
              courseId="demo"
              interactiveId="demo"
              title="Quick check: study strategies"
              questions={DEMO_QUESTIONS}
            />
          </div>
          <div
            role="tabpanel"
            id="ld-panel-step_reveal"
            aria-labelledby="ld-tab-step_reveal"
            hidden={tab !== "step_reveal"}
          >
            <StepRevealActivity
              title="Worked example: spacing your reviews"
              prompt={DEMO_WORKED.prompt}
              steps={DEMO_WORKED.steps}
            />
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-faint">
          In the product these are generated from your course content in one click —
          with per-question analytics, and answer keys that never reach the browser.
        </p>
      </div>
    </section>
  );
}
